import { DOMType, invalidReasonAttributeName, } from '@tencent/exeditor3-core';
import '@tencent/exeditor3-ui-kit/dist/toolbar-button.js';
import { pickFile } from '@tencent/exeditor3-ui-kit/dist/utils.js';
import { checkFileAccept } from './utils.js';
import { fileStyles } from './styles.js';
import { uploadingContainerClassName } from './constants.js';
const styles = fileStyles;
let uploadId = 1;
const defaultOption = {
    upload: () => {
        throw new Error('upload() method has not been set.');
    },
    accept: '',
    acceptFile: () => true,
    isNeedToUploadBySrc: () => false,
    hasTitle: true,
};
const uploadedCache = new Map();
const uploadFailedCache = new Map();
export class FilePlugin {
    constructor(options = {}) {
        this.name = 'file';
        this.nameCN = '文件';
        this.fileAttrsSpec = {
            uploading: {
                parse: (node) => node.dataset.uploading || '',
            },
            fail: {
                parse: (node) => node.dataset.fail,
            },
        };
        this.spec = {
            type: DOMType.BLOCK,
            matchSelector: `.${uploadingContainerClassName}[data-plugin="${this.name}"], span[data-type="file"]`,
            attrs: {
                info: {
                    parse: (node) => ({
                        src: node.dataset.src || '',
                        title: node.dataset.title || '',
                    }),
                },
                ...this.fileAttrsSpec,
            },
            toDOM: (node) => {
                const { uploading, info } = node.attrs;
                if (uploading || this.isNeedToUploadBySrc(info.src)) {
                    return this.wrapUploadingDOMRule(node.attrs, {
                        tag: 'span',
                        attributes: {},
                        children: '文件上传中...',
                    });
                }
                return {
                    tag: 'span',
                    attributes: {
                        'data-type': 'file',
                        'data-src': info.src,
                        'data-title': info.title,
                    },
                    children: [`文件名称: ${info.title}`, `文件地址: ${info.src}`].join('\n'),
                };
            },
            draggable: true,
        };
        this.editView = (node, getPos) => {
            if (node.attrs.uploading) {
                return this.editPlaceholderView(node, getPos);
            }
            const { info } = node.attrs;
            if (this.isNeedToUploadBySrc(info)) {
                this.uploadBySrc(info, getPos);
                return this.editPlaceholderView(node, getPos);
            }
            const container = document.createElement('div');
            const content = this.createContentElement(node.attrs.info);
            container.appendChild(content);
            return { dom: container };
        };
        this.set = (attrs) => {
            this.editor.selection().replaceWithNode(this, attrs);
        };
        this.insertByFiles = (files) => {
            const attrsList = [];
            Promise.all(files.map(async (file) => {
                var _a, _b, _c;
                const accepted = await ((_c = (_b = (_a = this.options).acceptFile) === null || _b === void 0 ? void 0 : _b.call(_a, file)) !== null && _c !== void 0 ? _c : true);
                if (!accepted) {
                    return;
                }
                const uploadingToken = this.getUploadToken();
                const title = this.options.hasTitle ? file.name || '' : '';
                const localSrc = window.URL.createObjectURL(file);
                const info = { src: localSrc, title, size: file.size };
                attrsList.push({
                    uploading: uploadingToken,
                    info,
                });
                const uploadFn = () => this.options.upload(file);
                this.upload(uploadFn, uploadingToken, info).then((resultAttrs) => {
                    if (resultAttrs.fail) {
                        return;
                    }
                    window.URL.revokeObjectURL(localSrc);
                });
            })).then(() => {
                this.insertFileAttrs(attrsList);
            });
        };
        this.reUpload = (attrs) => {
            const { uploading, info } = attrs;
            const uploadFn = uploadFailedCache.get(uploading);
            if (!uploadFn) {
                return;
            }
            uploadFailedCache.delete(uploading);
            this.batchUpdateLoading(uploading, { uploading, fail: '' });
            this.upload(uploadFn, uploading, info);
        };
        this.upload = async (uploadFn, uploading, basicInfo) => {
            let attrs = {};
            try {
                const uploadResult = await uploadFn();
                const metadata = await this.getMetadata(uploadResult, basicInfo, uploading);
                let info;
                if (typeof uploadResult === 'string') {
                    info = { ...basicInfo, ...metadata, src: uploadResult };
                }
                else {
                    info = { ...basicInfo, ...metadata, ...uploadResult };
                }
                uploadedCache.set(uploading, info);
                attrs = { uploading: '', info };
            }
            catch (ex) {
                uploadFailedCache.set(uploading, uploadFn);
                attrs = { fail: 'fail' };
            }
            this.batchUpdateLoading(uploading, attrs);
            return attrs;
        };
        this.options = {
            ...defaultOption,
            ...options,
        };
    }
    initEditor(editor) {
        this.editor = editor;
    }
    capabilities() {
        return {
            styles,
            commands: {
                set: this.set,
                insertByFiles: this.insertByFiles,
            },
            callbacks: {
                handleDrop: (event) => {
                    var _a;
                    const files = (_a = event.dataTransfer) === null || _a === void 0 ? void 0 : _a.files;
                    const allFiles = Array.from(files).filter((file) => checkFileAccept(this.options.accept, file));
                    if (allFiles.length === 0) {
                        return false;
                    }
                    event.preventDefault();
                    this.insertByFiles(allFiles);
                    return false;
                },
                handlePaste: (event) => {
                    const { clipboardData } = event;
                    if (!clipboardData) {
                        return false;
                    }
                    const allTypes = Array.from(clipboardData.types);
                    // html 编辑器已处理
                    if (allTypes.includes('text/html')) {
                        return false;
                    }
                    const allFiles = Array.from(clipboardData.items)
                        .map((data) => data.getAsFile())
                        .filter((file) => !!file)
                        .filter((file) => checkFileAccept(this.options.accept, file));
                    if (allFiles.length === 0) {
                        return false;
                    }
                    event.preventDefault();
                    this.insertByFiles(allFiles);
                    return false;
                },
            },
        };
    }
    renderToolbarItem() {
        const btn = document.createElement('exeditor-toolbar-button');
        Object.assign(btn, {
            innerHTML: '文件',
            label: this.editor.t(this.nameCN),
            onclick: () => {
                pickFile(this.options.accept, true).then((files) => {
                    if (!(files === null || files === void 0 ? void 0 : files.length)) {
                        return;
                    }
                    this.editor.focus();
                    this.insertByFiles(Array.from(files));
                });
            },
        });
        return btn;
    }
    getUploadId() {
        return uploadId++;
    }
    getUploadToken() {
        return `file-uploading-${this.name}-${this.getUploadId()}`;
    }
    insertFileAttrs(attrsList) {
        const beforeSelection = this.editor.selection();
        const beforePos = beforeSelection.to;
        // 判断是不是在行末且未选中内容
        const cursor = this.editor.view.state.doc.resolve(beforePos);
        const isParagraphEnd = beforeSelection.raw.empty && !cursor.nodeAfter;
        const nodes = beforeSelection.replaceWithNodes(this, attrsList);
        // 判断是不是位置未变化
        const afterSelection = this.editor.selection();
        const afterPos = afterSelection.to;
        if (isParagraphEnd && afterPos === beforePos) {
            const length = nodes.reduce((len, node) => node.nodeSize + len, 0);
            this.editor.selection(beforeSelection.to + length + 1);
        }
    }
    uploadBySrc(info, getPos) {
        if (typeof this.options.uploadBySrc !== 'function') {
            console.warn('Upload by src need function `options.uploadBySrc()`.');
            return;
        }
        const uploading = this.getUploadToken();
        const uploadFn = () => this.options.uploadBySrc(info.src);
        setTimeout(() => {
            this.editor.noHistory().patchNodeAttr(getPos(), { uploading });
            this.upload(uploadFn, uploading, info);
        });
    }
    batchUpdateLoading(uploading, attrs) {
        let pos = -1;
        let newPos = this.editor.getPosByAttr('uploading', uploading);
        while (newPos > pos) {
            this.editor.noHistory().patchNodeAttr(newPos, attrs);
            pos = newPos;
            newPos = this.editor.getPosByAttr('uploading', uploading, {
                from: pos + 1,
            });
        }
    }
    editPlaceholderView(node, getPos) {
        const t = this.editor.t.bind(this.editor);
        const isInline = this.options.inline;
        const { uploading, info } = node.attrs;
        setTimeout(() => {
            const cachedUploadResult = uploadedCache.get(uploading);
            const uploadFailed = Boolean(uploadFailedCache.get(uploading));
            const pos = getPos();
            if (cachedUploadResult) {
                this.editor.noHistory().patchNodeAttr(pos, {
                    uploading: '',
                    fail: '',
                    info: cachedUploadResult,
                });
            }
            else if (uploadFailed) {
                this.editor.noHistory().patchNodeAttr(pos, {
                    fail: 'fail',
                });
            }
        });
        const container = document.createElement(isInline ? 'span' : 'div');
        container.className = uploadingContainerClassName;
        const content = this.createContentElement(info);
        container.appendChild(content);
        const center = document.createElement('span');
        container.appendChild(center);
        const updateAttribute = (attrs) => {
            if (attrs.fail) {
                content.dataset.fail = 'fail';
                content.setAttribute(invalidReasonAttributeName, `${this.nameCN}上传失败`);
                const text = document.createTextNode(`${t(this.nameCN)}${t('')}${t('上传失败')}，`);
                const link = document.createElement('a');
                link.innerText = t('点击重试');
                link.onclick = (ev) => {
                    ev.preventDefault();
                    this.reUpload(attrs);
                };
                center.textContent = '';
                center.appendChild(text);
                center.appendChild(link);
            }
            else {
                content.setAttribute(invalidReasonAttributeName, `${t(this.nameCN)}${t('')}${t('正在上传中，请稍候')}`);
                delete content.dataset.fail;
                center.textContent = `${t(this.nameCN)}${t('')}${t('上传中...')}`;
            }
        };
        updateAttribute(node.attrs);
        return {
            dom: container,
            update: (node) => {
                if (!node.attrs.uploading) {
                    return false;
                }
                updateAttribute(node.attrs);
                return true;
            },
            destroy: () => {
                container.remove();
            },
        };
    }
    createContentElement(info) {
        const content = document.createElement('div');
        if (info.title) {
            const span = document.createElement('span');
            span.textContent = `文件名称: ${info.title}`;
            content.appendChild(span);
        }
        if (info.src) {
            const span = document.createElement('span');
            span.textContent = `文件地址: ${info.src}`;
            content.appendChild(span);
        }
        return content;
    }
    getMetadata(result, info) {
        if (typeof result === 'string') {
            return Promise.resolve({
                ...info,
                src: result,
            });
        }
        return Promise.resolve({
            ...info,
            ...result,
        });
    }
    wrapUploadingDOMRule(attrs, children) {
        const { uploading, fail } = attrs;
        const isInline = this.options.inline;
        return {
            tag: isInline ? 'span' : 'div',
            attributes: {
                class: uploadingContainerClassName,
                'data-plugin': this.name,
                'data-uploading': uploading,
                'data-fail': fail,
            },
            children,
        };
    }
    isNeedToUploadBySrc(info) {
        var _a, _b;
        return ((_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.isNeedToUploadBySrc) === null || _b === void 0 ? void 0 : _b.call(_a, info.src)) || false;
    }
}
//# sourceMappingURL=plugin.js.map